import {inject, Injectable} from '@angular/core';
import {UserDto} from "../repositories/dto/user.dto";
import {filter, map, retry} from "rxjs/operators";
import {BehaviorSubject, distinctUntilChanged, Observable, Subscription, tap} from "rxjs";
import {isDefined, isUuid, unsubscribe} from "../libs/utils/epic.utils";
import {EmailDto} from "../repositories/dto/email.dto";
import {UserRepo} from "../repositories/api/user.repo";
import {OrgUserDto} from "../repositories/dto/org-user.dto";
import {OverviewDto} from "../repositories/dto/overview-dto";
import {VoidDto} from "../repositories/dto/void.dto";
import {RepoProvider} from "../repositories/repo.provider";
import {AuthService} from "./auth/auth.service";

@Injectable({providedIn: 'root'})
export class UserService {

  private readonly authService: AuthService = inject(AuthService);
  private readonly userRepo: UserRepo = inject(RepoProvider).getRepo(UserRepo);

  private readonly user: BehaviorSubject<UserDto> = new BehaviorSubject<UserDto>(new UserDto());
  private readonly assignedOrgs: BehaviorSubject<OrgUserDto[]> = new BehaviorSubject<OrgUserDto[]>([]);
  private assignedOrgsSubs: Subscription | undefined;

  constructor() {
    this.loadAssignedOrgs();
    this.authService.getAuthenticatedSuccess().subscribe(() => {
      this.userRepo.readSelf().pipe(
        retry({delay: 1000}), // Repeat indefinitely with a 1s delay
        map((w) => w.getFirstData())
      ).subscribe((user) => this.user.next(user));
    });
  }

  /*
    * Ping is required to check whether the user is authenticated.
    * If not, a redirect to the login in the AuthService occurs.
    * retry - delay: Repeat indefinitely with a 1-second delay
    */
  public getPing(): Observable<void> {
    return this.userRepo.ping().pipe(retry({delay: 1000}), map(() => void 0));
  }

  public getUser(): Observable<UserDto> {
    return this.user.asObservable().pipe(filter((user) => isUuid(user.id)));
  }

  public getUserDistinct(): Observable<UserDto> {
    return this.getUser().pipe(distinctUntilChanged((prev, curr) => prev.id === curr.id));
  }

  public updateUser(user: UserDto): Observable<UserDto> {
    return this.userRepo.updateUser(user).pipe(
      map((w) => w.getFirstData()),
      tap((user) => this.user.next(user))
    );
  }

  // ##########################################################################
  // ##### Overview
  // ##########################################################################

  public getOverview(): Observable<OverviewDto> {
    return this.userRepo.getOverview(this.user.value).pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // ##### User > OrgUser > Org
  // ##########################################################################

  public getAssignedOrgs(): Observable<OrgUserDto[]> {
    return this.assignedOrgs.asObservable().pipe(filter((orgs) => orgs.length > 0));
  }

  public loadAssignedOrgs(): void {
    unsubscribe(this.assignedOrgsSubs);
    this.assignedOrgsSubs = this.getUserDistinct().subscribe((user) => {
      this.userRepo.readAssignedOrgs(user).pipe(
        retry({delay: 1000}), // Repeat indefinitely with a 1s delay
        map((w) => w.getDatas())
      ).subscribe((orgs) => this.assignedOrgs.next(orgs));
    });
  }

  public inviteAccept(orgUser: OrgUserDto): Observable<OrgUserDto> {
    // Set fake data so that the required fields are set during validation
    if (isDefined(orgUser.org)) {
      orgUser.org.name = 'InviteAcceptDummy';
      orgUser.org.countryCode = 'DE';
      orgUser.org.languageCode = 'de';
      orgUser.org.timezone = 'Europe/Berlin';
    }
    return this.userRepo.inviteAccept(orgUser).pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // ##### Emails
  // ##########################################################################

  public getEmails(user: UserDto): Observable<EmailDto[]> {
    return this.userRepo.readEmails(user).pipe(map((w) => w.getDatas()));
  }

  public addEmail(user: UserDto, email: EmailDto): Observable<EmailDto> {
    return this.userRepo.createEmail(user, email).pipe(map((w) => w.getFirstData()));
  }

  public deleteEmail(user: UserDto, email: EmailDto): Observable<VoidDto> {
    return this.userRepo.deleteEmail(user, email).pipe(map((w) => w.getFirstData()));
  }
}
